import { useState } from 'react'

import { Box, Spinner, useClassNames } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  ShiftManagementAutoScheduleStatusDocument,
  ShiftManagementCoverageSectionDocument,
  ShiftManagementCoverageShiftsDocument,
  ShiftManagementEnumsAutoSchedulerExecutionStatusEnum,
} from 'generated/resources/graphql'
import { CoveragesByDayDocument, CoveragesByHourDocument } from 'generated/resources/graphql'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import getFilterValue from 'modules/shift_management/components/ShiftManagement/Content/filters/getFilterValue'
import useFetchStats from 'modules/shift_management/components/ShiftManagement/MainPage/useFetchStats'
import { SHIFT_MANAGEMENT_FILTERS } from 'modules/shift_management/hooks/reducers/filter'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import useLocationIdsFilter from 'modules/shift_management/hooks/useLocationIdsFIlter'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import {
  AutomaticScheduleStatus,
  AutomaticScheduleStatusMessages,
} from 'modules/shift_management/lib/consts/ShiftManagementAutomation'
import { COVERAGE_SECTION_HEIGHT } from 'modules/shift_management/lib/consts/ShiftManagementCoverage'
import type { View } from 'modules/shift_management/lib/types'
import { ShiftsRow } from 'modules/shift_management/lib/types'
import Coverages from 'modules/shift_management/store/internal/collections/Coverages'
import CoveragesByDay from 'modules/shift_management/store/internal/collections/CoveragesByDay'
import UnassignedShifts from 'modules/shift_management/store/internal/collections/UnassignedShifts'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'

import { Column, ColumnsType, gridTemplateColumns } from '../utils/buildColumns'
import AutomationInProgress from './AutomationInProgress'
import Body from './Body'
import CoverageShiftsSummary from './Body/CoverageShiftsSummary'
import { HeaderOld } from './Header/indexOld'
import * as stylesRefs from './index.vanilla.css'

interface CoverageSectionProps {
  columns: ColumnsType
  view: View
  resizeHeight: number
}

const CoverageSectionOld = ({ columns, view, resizeHeight }: CoverageSectionProps) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [automationInProgress, setAutomationInProgress] = useState<boolean>(false)
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const { refetchData } = useFetchStats({ fetchPolicy: 'cache-first' })
  const locationIds = useLocationIdsFilter()
  const { company } = useCurrentSession()
  const [{ newShiftsToQuery }] = useSchedule()

  const cx = useClassNames(stylesRefs)
  const firstCellWidth = columns[0]?.minWidth || 's240'
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const newGraphQLVersion = hasFeatureFlag('dev_shift_management_graphql_migration')

  const { filters: employeeFilters } = useEmployeeMultiFilters(
    getFilterValue,
    LOCAL_STORAGE_KEYS.shiftEmployees
  )
  const locationFilter = employeeFilters.filter(
    (f) => f.values.field === SHIFT_MANAGEMENT_FILTERS.employeeLocation
  )
  const [filters] = useFilters()

  const locationId = Number(locationFilter.at(0)?.values?.value)
  const employeeIds = filters.employeeIds?.map((employeeId) => Number(employeeId))

  const rangeParams = {
    startAt: view.startAt,
    endAt: view.endAt,
    locationId,
  }

  const isSolvingInProgress = (
    status: ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  ) =>
    status === AutomaticScheduleStatus.SOLVING_ACTIVE ||
    status === AutomaticScheduleStatus.SOLVING_SCHEDULED

  const isSolvingCompleted = (
    status: ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  ) => status === AutomaticScheduleStatus.SOLVING_COMPLETED

  const isSolvingFailed = (
    status: ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  ) =>
    [
      AutomaticScheduleStatus.SOLVING_FAILED,
      AutomaticScheduleStatus.SOLVING_INCOMPLETE,
      AutomaticScheduleStatus.SOLVING_KILLED,
    ].includes(status)

  const { data, loading } = useQuery(ShiftManagementCoverageSectionDocument, {
    variables: rangeParams,
    skip: !newGraphQLVersion,
  })

  const { refetch } = useQuery(ShiftManagementCoverageShiftsDocument, {
    variables: {
      employeeIds,
      ...rangeParams,
    },
  })

  const [getCoverageCountersByDay, { data: coverageCountersByDayData }] =
    useLazyQuery(CoveragesByDayDocument)
  const [getCoverageCountersByHour, { data: coverageCountersByHourData }] =
    useLazyQuery(CoveragesByHourDocument)

  const {
    data: scheduleData,
    stopPolling,
    startPolling,
  } = useQuery(ShiftManagementAutoScheduleStatusDocument, {
    variables: {
      locationId: Number(locationId),
      first: 1,
      sortOrder: { field: 'updated_at', order: 'desc' },
    },
    skip: !locationId,
    pollInterval: 3000,
  })
  const [lastExecutionStatus, setLastExecutionStatus] = useState<
    ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  >(scheduleData?.shiftManagement?.autoSchedulerExecutionsConnection?.nodes?.at(0)?.status)

  useUnsafeEffect(() => {
    if (!scheduleData) return

    const status =
      scheduleData?.shiftManagement?.autoSchedulerExecutionsConnection?.nodes?.[0]?.status

    const statusChanged = status && status !== lastExecutionStatus

    if (statusChanged || isSolvingInProgress(status)) {
      handleStatusChange(status)
    }
  }, [scheduleData])

  useEffect(() => {
    if (automationInProgress) {
      startPolling(3000)
    } else {
      stopPolling()
    }
  }, [automationInProgress])

  const handleStatusChange = async (
    status: ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  ) => {
    if (isSolvingInProgress(status)) {
      setLastExecutionStatus(status)
      setAutomationInProgress(true)
      return
    }

    if (isSolvingCompleted(status)) {
      if (!lastExecutionStatus) {
        setAutomationInProgress(false)
        return
      }

      await refetchData()
      const { data: shiftData } = await refetch()
      const { shifts: resultShifts = [], restDays: resultRestDays = [] } =
        shiftData?.shiftManagement

      const shiftsToUpdate = resultShifts?.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift?.employee?.fullName || '',
          employeeAvatar: shift?.employee?.avatar?.url || '',
          employeeLocationId: shift?.employee?.locationId || 0,
        })
      )
      const restDaysToUpdate = resultRestDays?.map((restDay) =>
        RestDay.build({
          id: restDay.id,
          employee_id: restDay.employee?.id || 0,
          date: restDay.date,
          state: restDay.state,
          timezone: restDay.timezone || '',
          employeeFullName: restDay.employee?.fullName || '',
          employeeAvatar: restDay.employee?.avatar?.url || '',
        })
      )

      dispatchShiftsRows({ type: 'RESET_SHIFTS', shifts: shiftsToUpdate, view })
      dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays: restDaysToUpdate })
      setLastExecutionStatus(status)
      setAutomationInProgress(false)
      app.setFlash({
        type: 'correct',
        message:
          AutomaticScheduleStatusMessages.solving_completed() ??
          i18n.t('shift_management.coverage_section.automation.modal.responses.default_success'),
      })
      return
    }

    if (isSolvingFailed(status) && status) {
      setAutomationInProgress(false)
      if (!lastExecutionStatus) {
        setLastExecutionStatus(status)
        return
      }
      setLastExecutionStatus(status)

      app.setFlash({
        type: 'wrong',
        message:
          AutomaticScheduleStatusMessages[status]?.() ??
          i18n.t('shift_management.coverage_section.automation.modal.responses.default_error'),
      })
    }
  }

  const unassignedShifts: UnassignedShifts = useUnsafeMemo(
    () => UnassignedShifts.buildFromGraphQL(data?.shiftManagement?.unassignedShifts || []),
    [data?.shiftManagement.unassignedShifts]
  )

  useUnsafeEffect(() => {
    if (!newGraphQLVersion) return
    dispatchUnassignedShifts({ type: 'BOOTSTRAP', unassignedShifts })
  }, [newGraphQLVersion, dispatchUnassignedShifts, unassignedShifts])

  useUnsafeEffect(() => {
    if (view.isWeekly) return
    const commonVariables = {
      startAt: view.startAt,
      endAt: view.endAt,
      customFilters: filters.customFilters,
      employeeIds: shiftsRows.map((row: ShiftsRow) => Number(row.id)),
      logicOperator: filters.logicOperator,
      locationIds: locationIds ?? undefined,
      shiftIds: filters.shiftIds,
      terminated: filters.terminated,
    }
    if (view.isDaily) {
      getCoverageCountersByHour({
        variables: {
          ...commonVariables,
          usePercentages: false,
        },
      })
    } else {
      getCoverageCountersByDay({
        variables: {
          companyId: company.id,
          ...commonVariables,
        },
      })
    }
  }, [view, newShiftsToQuery, filters])

  const peopleData = useUnsafeMemo(() => {
    return view.isDaily
      ? Coverages.buildFromRequest(
          coverageCountersByHourData?.shiftManagement.coveragesConnection.nodes ?? []
        )
      : CoveragesByDay.buildFromRequest(
          coverageCountersByDayData?.shiftManagement.coveragesByDaysConnection.nodes ?? []
        )
  }, [coverageCountersByDayData, coverageCountersByHourData, view])

  if (loading)
    return (
      <Box alignItems="center" width="full" padding="s32" background="grey000White" height="s140">
        <Spinner />
      </Box>
    )

  return (
    <>
      <HeaderOld columns={columns} />
      <>
        <Box
          width="full"
          style={{
            minHeight: `${COVERAGE_SECTION_HEIGHT}px`,
          }}
        >
          {automationInProgress && <AutomationInProgress />}
          <Body
            columns={columns}
            view={view}
            setIsSubscribed={setAutomationInProgress}
            resizeHeight={resizeHeight}
          />
        </Box>
        <Box
          className={cx('gridHeader')}
          style={{
            ...gridTemplateColumns(firstCellWidth, view, 'content', 0),
          }}
        >
          {columns.map((heading: Column, index: number) =>
            index != 0 ? (
              <CoverageShiftsSummary
                key={index}
                date={heading.date}
                shiftsRows={shiftsRows}
                peopleData={peopleData}
              />
            ) : (
              <Box
                key={index}
                background="grey100"
                border={{
                  right: {
                    width: 's1',
                    style: 'solid',
                    color: 'grey300',
                  },
                }}
                className={cx('header', 'sticky')}
              />
            )
          )}
        </Box>
      </>
    </>
  )
}

export { CoverageSectionOld }
